import React from 'react';
import { Link } from 'react-router-dom';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FavoriteIcon from '@mui/icons-material/Favorite';

function timeAgo(dateStr) {
    if (!dateStr) return '';
    const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days} day${days !== 1 ? 's' : ''} ago`;
    const months = Math.floor(days / 30);
    if (months < 12) return `${months} month${months !== 1 ? 's' : ''} ago`;
    const years = Math.floor(months / 12);
    return `${years} year${years !== 1 ? 's' : ''} ago`;
}

const truncStyle = (lines) => ({
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: lines,
    WebkitBoxOrient: 'vertical',
});

// Strip markdown syntax to plain text
function stripMarkdown(md) {
    if (!md) return '';
    return md
        .replace(/^#{1,6}\s+/gm, '')       // headings
        .replace(/(\*{1,3}|_{1,3})(.*?)\1/g, '$2') // bold/italic
        .replace(/!\[.*?\]\(.*?\)/g, '')    // images
        .replace(/\[([^\]]*)\]\(.*?\)/g, '$1') // links
        .replace(/`{1,3}[^`]*`{1,3}/g, '') // inline/fenced code
        .replace(/^\s*[-*+]\s+/gm, '')      // unordered list markers
        .replace(/^\s*\d+\.\s+/gm, '')      // ordered list markers
        .replace(/^\s*>\s+/gm, '')          // blockquotes
        .replace(/\$\$?[^$]*\$\$?/g, '')   // LaTeX
        .replace(/\|.*\|/g, '')             // table rows
        .replace(/---+/g, '')               // horizontal rules
        .replace(/\n{2,}/g, ' ')            // collapse blank lines
        .replace(/\n/g, ' ')               // remaining newlines
        .replace(/\s{2,}/g, ' ')           // collapse whitespace
        .trim();
}

function ProjectCard({ project, colClass = "col-12" }) {
    const isSingleCol = colClass === "col-12";
    const bodyPreview = isSingleCol && project.body ? stripMarkdown(project.body).slice(0, 200) : '';

    return (
        <div className={colClass} key={project.id}>
            <div className={`project-card-custom zoom${isSingleCol ? ' project-card-wide' : ''}`}>
                <Link to={`/projects/${project.id}`}>
                    {project.picture_link && (
                        <div style={{
                            width: '100%',
                            height: isSingleCol ? 160 : 100,
                            borderRadius: '6px 6px 0 0',
                            overflow: 'hidden',
                            marginBottom: 10,
                        }}>
                            <img
                                src={project.picture_link}
                                alt=""
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                }}
                                onError={(e) => { e.currentTarget.parentElement.style.display = 'none'; }}
                            />
                        </div>
                    )}
                    <h3
                        style={{
                            color: 'var(--ink)',
                            fontWeight: 800,
                            fontSize: '1.3em',
                            margin: 0,
                            ...truncStyle(2),
                        }}
                    >
                        {project.title}
                    </h3>
                    {project.subtitle && (
                        <h5 style={{ margin: '10px 0 0', ...truncStyle(2) }}>
                            {project.subtitle}
                        </h5>
                    )}
                    {project.technology && (
                        <p
                            style={{
                                color: 'var(--brand)',
                                fontWeight: 600,
                                fontSize: '0.7em',
                                lineHeight: '1.4',
                                margin: '14px 0 0',
                                ...truncStyle(2),
                            }}
                        >
                            {project.technology}
                        </p>
                    )}
                    {project.skill && (
                        <p
                            style={{
                                color: 'var(--brand)',
                                fontWeight: 600,
                                fontSize: '0.7em',
                                lineHeight: '1.4',
                                margin: '8px 0 0',
                                ...truncStyle(2),
                            }}
                        >
                            {project.skill}
                        </p>
                    )}
                    {bodyPreview && (
                        <p
                            style={{
                                color: 'var(--muted)',
                                fontSize: '0.55em',
                                lineHeight: '1.5',
                                margin: '12px 0 0',
                                ...truncStyle(3),
                            }}
                        >
                            {bodyPreview}
                        </p>
                    )}
                    <p
                        style={{
                            color: 'var(--muted)',
                            fontSize: '0.55em',
                            margin: '10px 0 0',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                        }}
                    >
                        {project.view_count > 0 && (
                            <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                                <VisibilityIcon style={{ fontSize: 13 }} />
                                {project.view_count.toLocaleString()}
                            </span>
                        )}
                        {project.like_count > 0 && (
                            <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                                <FavoriteIcon style={{ fontSize: 12, color: '#ba3f9c' }} />
                                {project.like_count.toLocaleString()}
                            </span>
                        )}
                        {project.updated_at && (
                            <span>{timeAgo(project.updated_at)}</span>
                        )}
                    </p>
                </Link>
            </div>
        </div>
    );
}

export default ProjectCard;